/**
 * Central portfolio data — generated from Dulan's CV (Dulan_Anjelo_CV_FINAL.pdf).
 * Every fact on the site comes from this file. Update it here and the whole site follows.
 */

export type Palette = { from: string; via: string; to: string; accent: string };

const PORTRAIT = '/assets/portrait-main.webp';
const PORTRAIT_ALT = '/assets/portrait-side.webp';

export const profile = {
  fullName: 'Dulan Anjelo',
  displayName: 'Dulan Anjelo',
  firstName: 'DULAN',
  seriesTag: 'THE SERIES',
  originalLabel: 'A DULAN ORIGINAL',
  role: 'Engineer · Founder · Product Builder',
  tagline: ['Electrical & Electronics Engineer', 'Founder, Care Compass AI', 'Product Builder'],
  intro:
    'Electrical & Electronics Engineer who learns by building. Founder of Care Compass AI, leading 4 engineers, and General Services Manager at Opal HealthCare, leading 10.',
  statement:
    "I can't look at a product without wondering how it was built and how I'd build it better. I spent my degree turning assignments into things that actually ran, then watched nurses type the same note three times and started a company to fix it. Give me a real problem and a small team and I'll own it from the idea to the working result.",
  location: 'Melbourne, VIC',
  email: 'dulanangelo278@gmail.com',
  phone: '+61 402 077 024',
  links: {
    linkedin: 'https://www.linkedin.com/in/dulan-anjelo-76b01b33b',
  },
  resumePdf: '/assets/Dulan_Anjelo_CV.pdf',
  portrait: {
    src: '/assets/portrait-main-1400.webp',
    srcSet: `/assets/portrait-main-800.webp 800w, /assets/portrait-main-1400.webp 1400w, ${PORTRAIT} 2475w`,
    alt: 'Portrait of Dulan Anjelo',
  },
  portraitAlt: {
    src: '/assets/portrait-side-1400.webp',
    srcSet: `/assets/portrait-side-800.webp 800w, /assets/portrait-side-1400.webp 1400w, ${PORTRAIT_ALT} 1822w`,
    alt: 'Dulan Anjelo at the microphone',
  },
  avatar: '/assets/avatar.webp',
  heroBase: '/assets/hero-base.webp',
  heroChrome: '/assets/hero-helmet.webp',
  interests: ['AI Product Development', 'Agentic AI', 'Embedded Systems'],
};

export const education = [
  {
    school: 'Swinburne University of Technology',
    place: 'Melbourne',
    degree: 'Bachelor of Electrical & Electronics Engineering',
    period: '2022 – 2024',
    score: '4.0 GPA',
  },
  {
    school: 'Swinburne University of Technology',
    place: 'Melbourne',
    degree: 'Diploma of Engineering',
    period: '2021 – 2022',
    score: '30% Merit Scholarship',
  },
  {
    school: 'Cambridge International',
    place: 'GCE',
    degree: 'A Levels (1 A, 2 B) · O Levels (7 A)',
    period: '2018 – 2020',
    score: 'IELTS 7.5 (C1)',
  },
];

export const experience = [
  {
    company: 'Opal HealthCare',
    role: 'General Services Manager',
    place: 'Melbourne, VIC',
    period: 'Aug 2026 – Present',
    points: [
      'Promoted from General Services Officer to lead 10 staff across maintenance and hospitality services, managing rostering, safety and compliance.',
      'Manage stock purchasing and coordinate the movement and distribution of supplies to support daily operations.',
      'Coordinate closely with nursing and clinical leads on facility needs; ongoing exposure to frontline workflows keeps deepening my understanding of documentation, handover and incident reporting challenges.',
    ],
  },
  {
    company: 'Care Compass AI (startup)',
    role: 'Product & Engineering Lead · Founder',
    place: 'Melbourne, VIC',
    period: 'Feb 2026 – Present',
    points: [
      'Founded Care Compass AI after identifying documentation pain points firsthand while working in aged care.',
      'Translate real aged care workflows into AI assisted care plans, incident reports and ISBAR shift handovers that reduce duplicated entry and improve documentation consistency.',
      'Lead 4 engineers, define requirements, prioritise features and coordinate development across React, Python/FastAPI and MongoDB.',
      'Built the AI assisted prototype with Cursor and Emergent on Python/FastAPI and MongoDB. Integrate LLM APIs, role based access controls and rate limits, test API behaviour with pytest, and present the working prototype for stakeholder feedback.',
    ],
  },
  {
    company: 'Opal HealthCare',
    role: 'General Services Officer',
    place: 'Melbourne, VIC',
    period: 'Feb 2025 – Aug 2026',
    points: [
      'Hospitality services, facility upkeep, safety checks and day to day Care Community operations while coordinating with maintenance, nursing and hospitality teams.',
      'Working alongside nursing staff exposed the administrative burden around care plans, shift handovers and incident reports. That recurring frontline problem became the starting point for Care Compass AI.',
    ],
  },
  {
    company: 'Startek Technologies',
    role: 'Engineering Intern',
    place: 'Melbourne, VIC',
    period: 'Feb 2024 – Jun 2024',
    points: [
      'Validated Automatic Licence Plate Recognition (ALPR) accuracy through image and metadata analysis for truck weighing and towed unit systems.',
      'Checked captured vehicle registration data against system records, documented test results and reported discrepancies for engineering review and troubleshooting.',
    ],
  },
  {
    company: 'Ashen Motors',
    role: 'Electrical Engineering Intern',
    place: 'Sri Lanka',
    period: 'Aug 2021 – Feb 2022',
    points: [
      'Installed automotive sound systems, lighting systems and electrical accessories; worked with vehicle wiring, power distribution, fuses, grounding and control connections.',
      'Tested installations and diagnosed electrical, power and signal faults using practical troubleshooting methods.',
    ],
  },
];

export type Metric = { value: string; label: string };

export type Project = {
  id: string;
  title: string;
  year: string;
  genre: string;
  logline: string;
  stack: string[];
  build: string[];
  features: string[];
  metrics: Metric[];
  github?: string;
  palette: Palette;
  motif: 'shield' | 'flow' | 'tenants';
  art?: string;
};

const crimson: Palette = { from: '#24060b', via: '#6e0d1d', to: '#09070a', accent: '#ff3d5a' };
const amber: Palette = { from: '#1c1003', via: '#6b3c06', to: '#0a0806', accent: '#ffb547' };
const ocean: Palette = { from: '#04121f', via: '#0f4c6e', to: '#05080d', accent: '#4cc9ff' };
const violet: Palette = { from: '#120822', via: '#3d1a6e', to: '#07060c', accent: '#b98bff' };
const jade: Palette = { from: '#03150f', via: '#0d5a40', to: '#050a08', accent: '#46e3a8' };

export const projects: Project[] = [
  {
    id: 'care-compass-ai',
    art: '/assets/art/care-compass.webp',
    title: 'Care Compass AI',
    year: '2026',
    genre: 'Startup • AI • HealthTech',
    logline: 'AI assisted care plans, incident reports and ISBAR handovers for aged care. My startup, founded Feb 2026.',
    stack: ['React', 'Python / FastAPI', 'MongoDB', 'LLM APIs', 'RBAC', 'pytest'],
    build: [
      'Founded after seeing the documentation pain firsthand as a General Services Officer. Translated real aged care workflows into AI assisted care plans, incident reports and ISBAR shift handovers that cut duplicated entry and keep documentation consistent.',
      'Lead 4 engineers: define requirements, prioritise features and coordinate development across React, Python/FastAPI and MongoDB. Integrated LLM APIs, role based access control, rate limiting and a pytest suite.',
    ],
    features: ['AI assisted care plans', 'Incident reporting', 'ISBAR shift handovers', 'Medication conflict and compliance checks', 'Role based access control', 'Working prototype for stakeholder feedback'],
    metrics: [
      { value: '4', label: 'engineers led' },
      { value: 'Feb 2026', label: 'founded' },
      { value: '3', label: 'core workflows' },
      { value: 'Live', label: 'working prototype' },
    ],
    palette: crimson,
    motif: 'shield',
  },
  {
    id: 'panda-vision-ai',
    art: '/assets/art/panda.webp',
    title: 'Panda Vision AI',
    year: '2025',
    genre: 'AI • Motion Simulation • University',
    logline: 'Text prompts in, 3D motion graphics out, on a custom physics and mathematics based motion engine.',
    stack: ['Python', 'Three.js', 'LLM API', 'Physics engine'],
    build: [
      'Built a university prototype generating 3D motion graphics from text prompts using a custom physics and mathematics based motion engine, so generated scenes follow real dynamics instead of keyframes.',
      'Led four engineers building on the engine. Applied simulation and automation concepts to motion generation, developing and testing features through successive iterations. Launched it, got no paying users, learnt the most from it.',
    ],
    features: ['Prompt to scene pipeline', 'Physics driven motion engine', 'Iterative feature testing', 'Team of four engineers'],
    metrics: [
      { value: '4', label: 'engineers led' },
      { value: '0', label: 'paying users (honest)' },
      { value: '1', label: 'custom motion engine' },
    ],
    palette: violet,
    motif: 'flow',
  },
  {
    id: 'agentic-ai-trading',
    art: '/assets/art/trading.webp',
    title: 'Agentic AI Trading Workflow',
    year: '2025',
    genre: 'Agentic AI • APIs • Fintech',
    logline: 'Autonomous agents coordinating multi step workflows, extended into a sentiment driven crypto paper trading agent.',
    stack: ['Agentic AI', 'Robinhood API', 'Sentiment analysis', 'Paper trading'],
    build: [
      'Built an agentic AI workflow to study how APIs and autonomous agents coordinate multi step creative and analytical workflows: information retrieval, reasoning, decision making and action execution.',
      'Extended it with Robinhood API integration to explore crypto trading agents that generate trade decisions from market sentiment signals derived from social media buzz and other public information. Used paper trading data to train, test and refine the agent before any live capital use.',
    ],
    features: ['Multi step agent orchestration', 'Robinhood API integration', 'Market sentiment signals', 'Paper trading evaluation loop'],
    metrics: [
      { value: '0', label: 'live capital risked' },
      { value: '4', label: 'agent stages' },
    ],
    palette: jade,
    motif: 'tenants',
  },
  {
    id: 'ems-smart-dispatch',
    art: '/assets/art/ems.webp',
    title: 'EMS Smart Dispatch Platform',
    year: '2024',
    genre: 'Innovation & Technology • Google Maps API',
    logline: 'Location based ambulance matching and route planning, with hospitals notified before the patient arrives.',
    stack: ['Google Maps API', 'Routing', 'Workflow design'],
    build: [
      'Prototyped an emergency dispatch platform using the Google Maps API for location based ambulance matching and route planning.',
      'Designed information workflows so incident details and patient history reach crews before arrival, and mapped hospital capacity, routing and advance notification to coordinate patients, crews and hospitals.',
    ],
    features: ['Nearest ambulance matching', 'Route planning', 'Patient history to crews pre arrival', 'Hospital capacity and advance notification'],
    metrics: [
      { value: '3', label: 'user types mapped' },
      { value: '1', label: 'end to end flow' },
    ],
    palette: amber,
    motif: 'flow',
  },
  {
    id: 'railway-crossing',
    art: '/assets/art/railway.webp',
    title: 'School Zone Railway Crossing Controller',
    year: '2023',
    genre: 'Embedded • Arduino • Safety',
    logline: 'A physical scale model railway crossing with real time train detection, signalling and automated boom gates.',
    stack: ['Arduino', 'C++', 'Sensors', 'Actuators'],
    build: [
      'Built and tested a physical scale model railway crossing using an Arduino microcontroller, sensor inputs, electronic signalling and automated boom gate actuation.',
      'Programmed embedded control logic to detect approaching trains and coordinate road signals and boom gate sequencing in real time, applying safety requirements for vehicles and pedestrians.',
    ],
    features: ['Train detection', 'Signal and boom gate sequencing', 'Fail safe defaults', 'Lab tested sensors and actuators'],
    metrics: [
      { value: 'Real time', label: 'control loop' },
      { value: 'Fail safe', label: 'default state' },
    ],
    palette: ocean,
    motif: 'shield',
  },
  {
    id: 'wave-energy',
    art: '/assets/art/wave.webp',
    title: 'Renewable Wave Energy Generation System',
    year: '2024',
    genre: 'Capstone • MATLAB / Simulink • Power',
    logline: 'Final year capstone modelling a wave energy generator under changing ocean conditions.',
    stack: ['MATLAB', 'Simulink', 'Power systems', 'Lab analysis'],
    build: [
      'Designed and modelled a wave energy generation system using MATLAB and Simulink, supported by engineering laboratory analysis and measurement.',
      'Evaluated electrical generation, power conversion and system efficiency under changing operating conditions and documented findings for renewable energy integration and reliability.',
    ],
    features: ['Energy conversion model', 'Efficiency under varying sea states', 'Grid integration evaluation'],
    metrics: [
      { value: 'Capstone', label: 'final year project' },
    ],
    palette: jade,
    motif: 'flow',
  },
  {
    id: '5g-disruption',
    art: '/assets/art/5g.webp',
    title: '5G Signal Performance & Tower Disruption Analysis',
    year: '2024',
    genre: 'Telecommunications • Research Report',
    logline: 'How cellular tower disruption hits 5G performance, and what keeps the service running.',
    stack: ['Network analysis', 'Telecommunications', 'Technical writing'],
    build: [
      'Researched and authored a technical report on how cellular tower disruption can affect 5G network performance and service continuity.',
      'Analysed signal strength, latency, throughput, coverage and reliability across disruption scenarios and presented engineering recommendations for maintaining service continuity.',
    ],
    features: ['Disruption scenarios', 'Signal, latency, throughput analysis', 'Resilience and mitigation strategies'],
    metrics: [
      { value: '5', label: 'metrics analysed' },
    ],
    palette: violet,
    motif: 'tenants',
  },
];

export type Achievement = {
  id: string;
  title: string;
  org: string;
  detail: string;
  laurel: string;
  link?: string;
};

export const achievements: Achievement[] = [
  { id: 'gsm', title: 'Promoted to General Services Manager', org: 'Opal HealthCare', detail: 'Leading 10 staff across maintenance and hospitality services in a regulated aged care home.', laurel: 'Leadership' },
  { id: 'founder', title: 'Founded Care Compass AI', org: 'Startup · Feb 2026', detail: 'Leading 4 engineers to turn a frontline documentation problem into a working AI product.', laurel: 'Founder' },
  { id: 'appreciation', title: 'Employee Appreciation Award', org: 'Opal HealthCare', detail: 'Recognised for reliability and impact on daily Care Community operations.', laurel: 'Awarded' },
  { id: 'leaders', title: 'Emerging Leaders Award', org: 'Swinburne University of Technology', detail: 'Selected for the Emerging Leaders of Swinburne program.', laurel: 'Emerging Leader' },
  { id: 'scholarship', title: '30% Merit Based Scholarship', org: 'Swinburne University of Technology', detail: 'Awarded for a 4.0 GPA during the Diploma of Engineering.', laurel: '4.0 GPA' },
];

export type Certification = { issuer: string; name: string; link: string };

const forage = 'https://www.theforage.com/simulations';

export const certifications: Certification[] = [
  { issuer: 'JPMorgan Chase & Co.', name: 'Backend & API Engineering (Forage)', link: `${forage}?companies=jpmorgan` },
  { issuer: 'Commonwealth Bank', name: 'Cybersecurity Foundations (Forage)', link: `${forage}?companies=commonwealth-bank` },
  { issuer: 'Commonwealth Bank', name: 'Full-Stack Engineering (Forage)', link: `${forage}?companies=commonwealth-bank` },
  { issuer: 'Blackbird', name: 'Software Development Practices (Forage)', link: `${forage}?companies=blackbird` },
  { issuer: 'Siemens', name: 'Operations Industrial Engineering (Forage)', link: `${forage}?companies=siemens` },
  { issuer: 'Johnson & Johnson MedTech', name: 'Robotics and Controls (Forage)', link: `${forage}?companies=johnson-johnson` },
  { issuer: 'APA Group', name: 'Engineering for New Energy (Forage)', link: `${forage}?companies=apa-group` },
  { issuer: 'GE Aerospace', name: 'Explore Engineering (Forage)', link: `${forage}?companies=ge-aerospace` },
  { issuer: 'IELTS', name: 'Overall Band 7.5 (CEFR C1)', link: 'https://ielts.org/' },
];

export type Skill = { name: string; mono: string; note?: string };
export type SkillCategory = { id: string; title: string; subtitle: string; skills: Skill[] };

export const skillCategories: SkillCategory[] = [
  {
    id: 'backend',
    title: 'Backend & AI',
    subtitle: 'What Care Compass runs on',
    skills: [
      { name: 'Python / FastAPI', mono: 'Py', note: 'Primary' },
      { name: 'LLM API Integration', mono: 'Ai' },
      { name: 'REST API Integration', mono: 'Ap' },
      { name: 'Role-Based Access Control', mono: 'Rb' },
      { name: 'Pytest / API Testing', mono: 'Pt' },
      { name: 'AI-Assisted Development', mono: 'Ad' },
      { name: 'Cursor / Emergent', mono: 'Cu', note: 'AI tools' },
    ],
  },
  {
    id: 'frontend',
    title: 'Frontend',
    subtitle: 'Interfaces people actually use',
    skills: [
      { name: 'React', mono: 'Re' },
      { name: 'Tailwind CSS', mono: 'Tw' },
      { name: 'JavaScript', mono: 'Js' },
    ],
  },
  {
    id: 'data',
    title: 'Data',
    subtitle: 'Storage & analysis',
    skills: [
      { name: 'MongoDB / NoSQL', mono: 'Mg' },
      { name: 'Data Analysis', mono: 'Da' },
    ],
  },
  {
    id: 'engineering',
    title: 'Engineering',
    subtitle: 'The electrical side',
    skills: [
      { name: 'MATLAB / Simulink', mono: 'Ml' },
      { name: 'Arduino / Embedded Control', mono: 'Ar' },
      { name: 'Sensors / Actuators', mono: 'Sa' },
      { name: 'Electrical Testing & Troubleshooting', mono: 'Et' },
      { name: 'C++', mono: 'C+' },
    ],
  },
  {
    id: 'product',
    title: 'Product & Leadership',
    subtitle: 'Turning problems into shipped things',
    skills: [
      { name: 'Requirements & Prioritisation', mono: 'Rq' },
      { name: 'Technical Team Leadership', mono: 'Tl' },
      { name: 'Stakeholder Communication', mono: 'Sc' },
      { name: 'Safety & Compliance', mono: 'Sf' },
    ],
  },
  {
    id: 'interests',
    title: 'Interests',
    subtitle: 'Coming soon to the series',
    skills: [
      { name: 'AI Product Development', mono: 'Ai' },
      { name: 'Agentic AI', mono: 'Ag' },
      { name: 'Embedded Systems', mono: 'Em' },
    ],
  },
];

export const skillEvidence: Record<string, string[]> = {
  'Python / FastAPI': ['Care Compass AI', 'Panda Vision AI', 'JPMorgan Backend & API Engineering'],
  'LLM API Integration': ['Care Compass AI', 'Panda Vision AI', 'Agentic AI Trading Workflow'],
  'REST API Integration': ['Care Compass AI', 'Agentic AI Trading Workflow', 'JPMorgan Backend & API Engineering'],
  'Role-Based Access Control': ['Care Compass AI', 'CommBank Cybersecurity Foundations'],
  'Pytest / API Testing': ['Care Compass AI'],
  'AI-Assisted Development': ['Care Compass AI', 'Agentic AI Trading Workflow'],
  'Cursor / Emergent': ['Care Compass AI prototype', 'This portfolio'],
  React: ['Care Compass AI', 'CommBank Full-Stack Engineering'],
  'Tailwind CSS': ['Care Compass AI'],
  JavaScript: ['Care Compass AI', 'Panda Vision AI'],
  'MongoDB / NoSQL': ['Care Compass AI'],
  'Data Analysis': ['Startek ALPR validation', '5G Disruption Analysis'],
  'MATLAB / Simulink': ['Wave Energy Generation System', '5G Disruption Analysis'],
  'Arduino / Embedded Control': ['Railway Crossing Controller'],
  'Sensors / Actuators': ['Railway Crossing Controller', 'J&J MedTech Robotics and Controls'],
  'Electrical Testing & Troubleshooting': ['Ashen Motors', 'Startek Technologies'],
  'C++': ['Railway Crossing Controller'],
  'Requirements & Prioritisation': ['Care Compass AI', 'EMS Smart Dispatch Platform'],
  'Technical Team Leadership': ['Care Compass AI (4 engineers)', 'Opal HealthCare (10 staff)'],
  'Stakeholder Communication': ['Care Compass AI', 'Opal HealthCare'],
  'Safety & Compliance': ['Opal HealthCare', 'Railway Crossing Controller'],
  'Agentic AI': ['Agentic AI Trading Workflow'],
  'AI Product Development': ['Care Compass AI'],
  'Embedded Systems': ['Railway Crossing Controller'],
};

export type Episode = {
  code: string;
  title: string;
  description: string;
  tags: string[];
  runtime: string;
  palette: Palette;
};

export type Season = {
  number: number;
  title: string;
  period: string;
  synopsis: string;
  episodes: Episode[];
};

export const seasons: Season[] = [
  {
    number: 1,
    title: 'The Foundation',
    period: '2018 – 2022',
    synopsis: 'Cambridge A and O Levels, then the Diploma of Engineering at Swinburne on a merit scholarship.',
    episodes: [
      { code: 'S01 E01', title: 'The Student', description: 'Cambridge GCE O Levels (7 A) and A Levels (1 A, 2 B). IELTS Overall Band 7.5.', tags: ['Cambridge', 'IELTS 7.5'], runtime: '2018 – 2020', palette: amber },
      { code: 'S01 E02', title: 'The Scholar', description: 'Diploma of Engineering at Swinburne. Awarded the 30% Merit Based Scholarship for a 4.0 GPA.', tags: ['Swinburne', '4.0 GPA', 'Scholarship'], runtime: '2021 – 2022', palette: violet },
      { code: 'S01 E03', title: 'The First Wires', description: 'Electrical Engineering Intern at Ashen Motors: wiring, power distribution, fault diagnosis in real vehicles.', tags: ['Internship', 'Automotive'], runtime: 'Aug 2021 – Feb 2022', palette: ocean },
    ],
  },
  {
    number: 2,
    title: 'The Engineer',
    period: '2022 – 2024',
    synopsis: 'Bachelor of Electrical & Electronics Engineering. Every unit turned into something that actually ran.',
    episodes: [
      { code: 'S02 E01', title: 'The Builder', description: 'EMS Smart Dispatch Platform on the Google Maps API, a railway crossing controller on Arduino, and a 5G disruption analysis.', tags: ['Google Maps API', 'Arduino', 'Telecoms'], runtime: '2023 – 2024', palette: amber },
      { code: 'S02 E02', title: 'The Intern', description: 'Engineering Intern at Startek Technologies validating ALPR accuracy for truck weighing and towed unit systems.', tags: ['ALPR', 'Data quality'], runtime: 'Feb – Jun 2024', palette: ocean },
      { code: 'S02 E03', title: 'The Capstone', description: 'Renewable Wave Energy Generation System in MATLAB/Simulink. Swinburne Emerging Leaders Award.', tags: ['MATLAB', 'Simulink', 'Emerging Leaders'], runtime: '2024', palette: jade },
    ],
  },
  {
    number: 3,
    title: 'The Frontline',
    period: '2025 – 2026',
    synopsis: 'General Services Officer at Opal HealthCare. Watching nurses type the same thing three times.',
    episodes: [
      { code: 'S03 E01', title: 'The Officer', description: 'Hospitality, facility upkeep and safety checks in a regulated aged care home. Employee Appreciation Award.', tags: ['Aged care', 'Compliance'], runtime: 'Feb 2025 – Aug 2026', palette: crimson },
      { code: 'S03 E02', title: 'The AI Experiments', description: 'Panda Vision AI (text to 3D motion) and an agentic AI trading workflow, both at uni. Four engineers led, zero paying users, lessons for life.', tags: ['Panda Vision', 'Agentic AI'], runtime: '2025', palette: violet },
    ],
  },
  {
    number: 4,
    title: 'The Founder',
    period: '2026 – Present',
    synopsis: 'Care Compass AI is founded and a promotion to General Services Manager follows.',
    episodes: [
      { code: 'S04 E01', title: 'The Problem', description: 'Founded Care Compass AI in Feb 2026 to take the documentation pain out of aged care.', tags: ['Startup', 'HealthTech'], runtime: 'Feb 2026', palette: crimson },
      { code: 'S04 E02', title: 'The Team', description: 'Leading 4 engineers across React, Python/FastAPI and MongoDB. LLM APIs, RBAC, rate limiting, pytest.', tags: ['4 engineers', 'FastAPI', 'MongoDB'], runtime: '2026', palette: ocean },
      { code: 'S04 E03', title: 'The Manager', description: 'Promoted to General Services Manager at Opal HealthCare, leading 10 staff across two departments.', tags: ['10 staff', 'Leadership'], runtime: 'Aug 2026 – Present', palette: amber },
    ],
  },
  {
    number: 5,
    title: "What's Next",
    period: 'Now streaming',
    synopsis: 'AI product development, agentic systems and embedded engineering point to the next arc.',
    episodes: [
      { code: 'S05 E01', title: 'The Next Chapter', description: 'Shipping Care Compass AI to real care homes and building products where the engineering and the people meet.', tags: ['AI Products', 'Agentic AI', 'Embedded'], runtime: 'In production', palette: violet },
    ],
  },
];

export type TopPick = { label: string; title: string; detail: string; palette: Palette };

export const topPicks: TopPick[] = [
  { label: 'Sparks joy', title: 'Jersey (2019)', detail: 'A cricketer written off by everyone picks up the bat again. The train station scene wrecks me every time.', palette: crimson },
  { label: 'How I discover', title: 'Talk to hands, not titles', detail: 'Write the job to be done in the user\'s own words before any solution exists.', palette: amber },
  { label: 'How I use AI', title: 'AI drafts, humans decide', detail: 'Claude and GPT red team my specs. The judgement about what to build stays mine.', palette: violet },
  { label: 'How I prioritise', title: 'Leverage beats scorecards', detail: 'Ship the thing that makes everything else cheaper to build.', palette: jade },
  { label: 'How I build safety', title: 'Fail safe by default', detail: 'If a sensor dies, the gate closes. Learnt on an Arduino, applied in aged care.', palette: ocean },
  { label: 'Honest stat', title: '0 paying users', detail: 'Panda Vision launched and nobody paid. Best product lesson I have.', palette: crimson },
  { label: 'Trust & safety', title: 'Precision over recall', detail: 'A false positive flags an innocent driver. Every automated call needs a human path.', palette: amber },
  { label: 'Where', title: 'Melbourne via Sri Lanka', detail: 'First wires at Ashen Motors in Sri Lanka, first team in Melbourne.', palette: violet },
  { label: 'Habit', title: 'Learns by building', detail: 'Seven uni projects that actually ran, not slide decks.', palette: jade },
  { label: 'Belief', title: 'Docs are the product', detail: 'If the engine is undocumented, the team\'s work breaks. Learnt leading four engineers.', palette: ocean },
];

export type IntroSlide = { kicker: string; title: string; lines: string[]; chips?: string[] };

export const introSlides: IntroSlide[] = [
  { kicker: 'Education', title: 'Electrical & Electronics Engineering', lines: ['Swinburne University of Technology', '2021 – 2024 · Diploma then Bachelor'], chips: ['4.0 GPA', 'Merit Scholarship'] },
  { kicker: 'Now', title: 'Founder. Care Compass AI.', lines: ['Product & Engineering Lead · Feb 2026 – Present', 'Leading 4 engineers · React, FastAPI, MongoDB, LLM APIs'], chips: ['Startup', 'HealthTech', 'AI'] },
  { kicker: 'Also now', title: 'General Services Manager', lines: ['Opal HealthCare · Aug 2026 – Present', 'Leading 10 staff across maintenance and hospitality'] },
  { kicker: 'Projects', title: 'Seven Originals', lines: ['Care Compass AI · Panda Vision AI · Agentic trading agent', 'EMS dispatch · Railway crossing · Wave energy · 5G analysis'] },
  { kicker: 'Skills', title: 'Python first.', lines: ['FastAPI, LLM APIs, REST, RBAC, pytest · React, Tailwind', 'MongoDB · MATLAB/Simulink · Arduino · C++'], chips: ['Python', 'React', 'MongoDB', 'MATLAB', 'Arduino'] },
  { kicker: 'Moments', title: 'Top Moments', lines: ['Employee Appreciation Award · Opal HealthCare', 'Emerging Leaders Award · Swinburne', '30% Merit Based Scholarship · 4.0 GPA'] },
  { kicker: 'Certified', title: '8 Job Simulations', lines: ['JPMorgan · Commonwealth Bank · Blackbird · Siemens', 'J&J MedTech · APA Group · GE Aerospace · IELTS 7.5'] },
];

export type ProfileId = 'dulan';
export type SectionId = 'about' | 'journey' | 'originals' | 'picks' | 'skills' | 'moments' | 'story';

export const viewerProfiles: {
  id: ProfileId;
  name: string;
  blurb: string;
  color: string;
  order: SectionId[];
}[] = [
  { id: 'dulan', name: 'Dulan', blurb: 'The full series, in order', color: '#e5132b', order: ['about', 'journey', 'originals', 'picks', 'skills', 'moments', 'story'] },
];

export const sectionMeta: Record<SectionId, { nav: string; card: string; meta: string; palette: Palette }> = {
  about: { nav: 'About', card: 'About Me', meta: 'The Pilot • Education & experience', palette: violet },
  journey: { nav: 'Journey', card: 'My Journey', meta: `${seasons.length} Seasons • ${seasons.reduce((n, s) => n + s.episodes.length, 0)} Episodes`, palette: amber },
  originals: { nav: 'Originals', card: 'My Projects', meta: `${projects.length} Originals • 2023 – 2026`, palette: crimson },
  picks: { nav: 'Top 10', card: '10 Things About Me', meta: 'Character • principles • what sparks joy', palette: jade },
  skills: { nav: 'Skills', card: 'My Skills', meta: `${skillCategories.length} Categories`, palette: ocean },
  moments: { nav: 'Moments', card: 'My Achievements', meta: `${achievements.length} Moments • ${certifications.length} Certifications`, palette: crimson },
  story: { nav: 'Resume', card: 'The Full Story', meta: 'CV • View & download', palette: violet },
};
